import * as React from 'react'
import { useDispatch } from 'react-redux'

import { emailTranscript } from '../../actions/online'
import EscapeHandler from '../../lib/escape-handler'
import { strings } from '../../lib/localization'
import { RootState } from '../../types/rootstate'
import { MarkdownTransformer } from '../markdown-transformer'

import '../../sass/online/main.scss'
import '../../sass/online/email-transcript.scss'

const validateEmail = (email: string): boolean => {
  const re =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/

  return re.test(email)
}

interface EmailTranscriptProps {
  onClose: () => void
}

const EmailTranscript: React.FC<EmailTranscriptProps> = ({ onClose }) => {
  const dispatch = useDispatch()

  const [email, setEmail] = React.useState<string>('')
  const [hasError, setHasError] = React.useState<boolean>(false)
  const [hasSent, setHasSent] = React.useState<boolean>(false)

  const submitForm = () => {
    if (!validateEmail(email)) {
      setHasError(true)
      return
    }

    dispatch(emailTranscript(email))
    setHasError(false)
    setHasSent(true)
  }

  const handleEmailKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()

      const isValid = validateEmail(email)
      setHasError(isValid)

      if (isValid) {
        setHasSent(true)
        submitForm()
      }
    }
  }

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (validateEmail(email)) {
      setHasError(false)
    }

    setEmail(e.target.value)
  }

  const handleSubmitKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    if (e.key === 'Enter' || e.key === ' ') submitForm()
  }

  const handleSubmitClick = () => submitForm()
  const handleEscape = () => onClose()
  const handleCloseClick = () => onClose()
  const handleCloseKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'Enter' || e.key === ' ') onClose()
  }

  const emailInputRef = React.useRef<HTMLInputElement>(null)
  React.useEffect(() => {
    setTimeout(() => {
      emailInputRef.current?.focus()
    })

    EscapeHandler.registerEscapeListener(handleEscape)

    return () => EscapeHandler.removeEscapeListener(handleEscape)
  }, [])

  return (
    <div className="email-transcript">
      <div className="email-transcript-container">
        <div className="container">
          <div className="row">
            <div className="col-xs-12">
              {hasSent && (
                <>
                  <div className="email-transcript-success-icon">
                    <i className="material-icons">check_circle</i>
                  </div>

                  <div className="desc" style={{ textAlign: 'center' }}>
                    <MarkdownTransformer
                      value={strings.emailConversationSent}
                    />
                  </div>
                </>
              )}

              {!hasSent && (
                <div className="desc">
                  <MarkdownTransformer value={strings.emailConversation} />
                </div>
              )}
            </div>

            {!hasSent && (
              <div className="col-xs-12">
                <div className="input-line input-textfield">
                  <label htmlFor="email" dir="auto">
                    <MarkdownTransformer
                      value={strings.emailConversationEmail}
                    />
                  </label>
                  <div className="input-wrapper">
                    <input
                      id="email"
                      className={hasError ? 'email-error' : ''}
                      type="text"
                      onChange={handleEmailChange}
                      placeholder={strings.emailConversationEmailPlaceholder}
                      onKeyDown={handleEmailKeyDown}
                      ref={emailInputRef}
                      aria-required
                      aria-invalid={hasError}
                      aria-describedby={hasError ? 'email-error' : ''}
                    />
                  </div>
                  {hasError && (
                    <div id="email-error" className="input-error" role="alert">
                      <i className="material-icons input-error-icon">error</i>
                      <span>
                        <MarkdownTransformer
                          value={strings.emailConversationEmailValidationError}
                        />
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="col-xs-12">
              <div className="email-transcript-buttons">
                <button
                  className="button secondary accentSecondary"
                  tabIndex={0}
                  onClick={handleCloseClick}
                  onKeyDown={handleCloseKeyDown}
                >
                  <MarkdownTransformer value={strings.emailTranscriptClose} />
                </button>

                {!hasSent && (
                  <button
                    id="submitButton"
                    className="button primary accentSecondary"
                    tabIndex={0}
                    onClick={handleSubmitClick}
                    onKeyDown={handleSubmitKeyDown}
                  >
                    <MarkdownTransformer value={strings.emailTranscriptSend} />
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export { EmailTranscript }
